import { generateSpreadsheetViewConfig } from "datasheets/views/spreadsheet/SpreadsheetViewConfig";
import { ViewFactory } from "../ViewFactory";
import {
  DataSet,
  View,
  ViewKey,
  Workbook,
  WorkbookOperational,
} from "../core/Workbook";
import { PersistentKeyMapper, filterDict } from "../utils/dictionaryUtils";
import { newView } from "./new";
import { getOwnedDatasetId, viewOwnsDataset } from "./viewOwnsDataset";
import { uuid } from "../utils/uuid";
import { copyDataset, copyView } from "./replicate";
import { deepCopyWorkbook } from "./deepCopy";

// Following functions operate on workbook in place. Pass in deepCopy(workbook) to clone to new workbook
// TODO: make all functions have immutable parameters

/////////////////////
// Add Elements
export function workbookAddView(workbook: Workbook, viewType?: string | null) {
  const newWorkbook: Workbook = deepCopyWorkbook(workbook);
  const viewName = "Sheet" + (workbook.operational.nextSheetNumber + 1);
  const initialViewConfig = !viewType
    ? generateSpreadsheetViewConfig(viewName, newWorkbook)
    : ViewFactory.generateConfig(viewType as string, viewName, newWorkbook);
  const view: View = newView(viewName, initialViewConfig);
  const operational = {
    ...newWorkbook.operational,
    ...{ nextSheetNumber: newWorkbook.operational.nextSheetNumber + 1 },
  };
  const viewOrder = [...workbook.data.viewOrder, ...[view.id]];
  const views = {
    ...newWorkbook.data.views,
    ...{ [view.id]: view },
  };
  const datasets = newWorkbook.data.datasets;
  return {
    ...newWorkbook,
    ...{
      operational: operational,
      data: { viewOrder: viewOrder, views: views, datasets: datasets },
    },
  };
}

export function workbookDuplicateView(workbook: Workbook, viewId: string) {
  const viewToCopy = workbook.data.views[viewId];
  const newViewId = uuid();
  let datasets, newDataset, propertyRemap: PersistentKeyMapper;
  if (viewOwnsDataset(viewToCopy.config)) {
    const datasetId = getOwnedDatasetId(viewToCopy.config);
    const datasetToCopy = workbook.data.datasets[datasetId];
    [newDataset, propertyRemap] = copyDataset(datasetToCopy);
    datasets = {
      ...workbook.data.datasets,
      ...{ [newDataset.id]: newDataset },
    };
  } else {
    datasets = { ...workbook.data.datasets };
    propertyRemap = new PersistentKeyMapper((key) => uuid());
    // Ensure datasets don't get remapped
    Object.keys(datasets).forEach((datasetId) => {
      propertyRemap._map[datasetId] = datasetId;
    });
  }
  const newView = copyView(newViewId, viewToCopy, propertyRemap);
  const views = {
    ...workbook.data.views,
    ...{ [newView.id]: newView },
  };
  const tabs: string[] = [...workbook.data.viewOrder];
  tabs.push(newView.id);

  const newWorkbook: Workbook = {
    ...workbook,
    ...{
      operational: {
        ...workbook.operational,
        ...{ nextSheetNumber: workbook.operational.nextSheetNumber + 1 },
      },
      data: {
        datasets: datasets,
        views: views,
        viewOrder: tabs,
      },
    },
  };
  return newWorkbook;
}

/////////////////////
// Update Elements

export const workbookSetOrUpdateView = (
  workbook: Workbook,
  view: View
): Workbook => {
  const views: { [key: string]: View } = {
    ...workbook.data.views,
  };
  views[view.id] = view;
  const newViewOrder = [...workbook.data.viewOrder];
  if (!newViewOrder.includes(view.id)) {
    newViewOrder.push(view.id);
  }
  workbook.data = {
    ...workbook.data,
    ...{ views: views, viewOrder: newViewOrder },
  };
  return workbook;
};

export const workbookSetOrUpdateDataset = (
  workbook: Workbook,
  dataset: DataSet
): Workbook => {
  const datasets: { [key: string]: DataSet } = {
    ...workbook.data.datasets,
  };
  datasets[dataset.id] = dataset;
  workbook.data = { ...workbook.data, ...{ datasets: datasets } };
  return workbook;
};

/////////////////////
// Delete elements

export const workbookDeleteViewAndDataSet = (
  workbook: Workbook,
  viewId: string
): Workbook => {
  // Can only delete if it will leave a remaining view
  if (Object.keys(workbook.data.views).length <= 1) {
    throw Error("Cannot delete last remaining view");
  }
  const view = workbook.data.views[viewId];
  const datasetId = viewOwnsDataset(view.config)
    ? getOwnedDatasetId(view.config)
    : null;
  const views = filterDict(workbook.data.views, (key) => key !== viewId);
  const datasets = filterDict(
    workbook.data.datasets,
    (key) => key !== datasetId
  );
  const tabs = workbook.data.viewOrder.filter((key) => key !== viewId);
  const currentView = Object.keys(views)[0];
  return {
    ...workbook,
    ...{
      data: { views: views, datasets: datasets, viewOrder: tabs },
      operational: {
        ...workbook.operational,
        ...{
          selectedView: currentView,
        },
      },
    },
  };
};

/////////////////////
// Actions

export const workbookSwitchToView = (
  workbook: Workbook,
  targetViewId: ViewKey
) => {
  const operational: WorkbookOperational = {
    ...workbook.operational,
    ...{ selectedView: targetViewId },
  };
  workbook.operational = operational;
  return workbook;
};

export const workbookSwitchToTab = (workbook: Workbook, tabIdx: number) => {
  return workbookSwitchToView(workbook, workbook.data.viewOrder[tabIdx]);
};
